import { IaDrawer } from "ia-drawer";
import { EVENTS, subscribe } from "ia-util-pubsub";
import IaCartAPI from "ia-cart-api";

class IaCart extends IaDrawer {
  constructor() {
    super();
    window.iaCartAPI = new IaCartAPI({ sectionId: this.dataset.sectionId });
    window.iaCartAPI.cart = JSON.parse(this.querySelector("#ia-cart-json").textContent);
  }

  connectedCallback() {
    super.connectedCallback?.();
    // console.log(">>> ia-cart loaded");
    subscribe(EVENTS.cartBeforeChange, this.preload.bind(this));
    subscribe(EVENTS.cartChanged, this.preloadDone.bind(this));
    subscribe(EVENTS.cartChanged, this.updateBlocks.bind(this));
    subscribe(EVENTS.cartChanged, this.removeOrphanFreeItems.bind(this));
    subscribe(EVENTS.cartChanged, this.removeIncompleteBundleGifts.bind(this));
    // Guard the initial server-rendered cart too (no change event fires on load).
    this.removeOrphanFreeItems();
    this.removeIncompleteBundleGifts();
    // Auto-open when arriving with ?open_cart=1 (e.g. the /cart -> home redirect).
    this.openFromUrl();
  }

  // Opens the drawer when the URL carries ?open_cart=1, then strips the flag so
  // a refresh, back navigation, or shared link doesn't re-open it. Deferred to
  // window load so window.iaTheme (sticky helpers used by open()) is ready.
  openFromUrl() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    if (!params.has("open_cart")) return;

    params.delete("open_cart");
    const qs = params.toString();
    const cleanUrl =
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
    window.history.replaceState({}, "", cleanUrl);

    if (document.readyState === "complete") {
      this.open();
    } else {
      window.addEventListener("load", () => this.open(), { once: true });
    }
  }

  // A bundle's gift-with-purchase is only earned while ALL of that bundle's
  // components are still in the cart. Bundle lines share a _bundle_id; the gift
  // line additionally carries _gwp_bundle and _bundle_count (how many components
  // it requires). If a component has been removed, the present count drops below
  // the required count and we revoke the gift. Exposed as data-bundle-* in
  // ia-cart-items.liquid.
  removeIncompleteBundleGifts(event) {
    if (this._removingBundleGift) return;
    const dom = event?.detail?.cartDom || this;
    const items = Array.from(dom.querySelectorAll("ia-cart-item"));
    if (!items.length) return;

    // Count the component lines present for each bundle instance (gift excluded).
    const presentComponents = {};
    items.forEach((el) => {
      const bundleId = el.dataset.bundleId;
      if (bundleId && el.dataset.bundleGift !== "true") {
        presentComponents[bundleId] = (presentComponents[bundleId] || 0) + 1;
      }
    });

    const orphanGiftKeys = items
      .filter((el) => el.dataset.bundleGift === "true" && el.dataset.bundleId)
      .filter((el) => {
        const required = parseInt(el.dataset.bundleCount, 10) || 0;
        return (presentComponents[el.dataset.bundleId] || 0) < required;
      })
      .map((el) => el.dataset.key)
      .filter(Boolean);
    if (!orphanGiftKeys.length) return;

    this._removingBundleGift = true;
    const updates = {};
    orphanGiftKeys.forEach((key) => {
      updates[key] = 0;
    });
    Promise.resolve(window.iaCartAPI?.updateCart({ data: { updates } }))
      .catch(() => {})
      .then(() => {
        this._removingBundleGift = false;
      });
  }

  // Free products must never stand alone in the cart/checkout. Whenever the cart
  // contains only free-tagged items (no paid item left), remove them. Free items
  // are flagged with data-free-product="true" in ia-cart-items.liquid.
  removeOrphanFreeItems(event) {
    if (this._removingFree) return;
    const dom = event?.detail?.cartDom || this;
    const items = Array.from(dom.querySelectorAll("ia-cart-item"));
    if (!items.length) return;

    const freeKeys = items
      .filter((el) => el.dataset.freeProduct === "true")
      .map((el) => el.dataset.key)
      .filter(Boolean);
    if (!freeKeys.length) return;

    const paidCount = items.length - freeKeys.length;
    if (paidCount > 0) return;

    this._removingFree = true;
    const updates = {};
    freeKeys.forEach((key) => {
      updates[key] = 0;
    });
    Promise.resolve(window.iaCartAPI?.updateCart({ data: { updates } }))
      .catch(() => {})
      .then(() => {
        this._removingFree = false;
      });
  }

  get preloading() {
    return this.hasAttribute("data-preloading");
  }

  set preloading(value) {
    this.toggleAttribute("data-preloading", value);
  }

  preload(e) {
    if (this.preloading) return;
    this.preloading = true;
    this.classList.add("loading");
    
    setTimeout(() => {
      if (this.preloading) {
        this.preloadDone();
      }
    }, 3000);

    // Update loading state for items that are being updated
    if (e?.detail?.updates) {
      Object.keys(e.detail.updates).forEach(key => {
        const item = this.querySelector(`ia-cart-item[data-key="${key}"]`);
        if (item) {
          item.classList.add("is-loading");
        }
      });
    }
    if (e?.detail?.changes?.id) {
      const item = this.querySelector(`ia-cart-item[data-key="${e.detail.changes.id}"]`);
      if (item) {
        item.classList.add("is-loading");
      }
    }
  }

  preloadDone(event) {
    this.preloading = false;
    this.classList.remove("loading");
  }

  updateBlocks(e) {
    const html = e.detail?.cartDom;
    const blocks = this.querySelectorAll('[data-re-render]');
    blocks.forEach(block => {
      if (block.id) {
        const newBlock = html.querySelector(`#${block.id}`);
        if (newBlock) {
          block.outerHTML = newBlock.outerHTML;
        }
      }
    });
  }

  open() {
    super.open();
    document.body.classList.add("is-cart-open");
  }

  close() {
    super.close();
    document.body.classList.remove("is-cart-open");
  }
}

class IaCartItem extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    this.ts = this.dataset.ts || null;
    this.removeButton = this.querySelector(".js-cart-item-remove");
    this.qtyPicker = this.querySelector("ia-product-qty");
    if (this.qtyPicker) {
      this.unsubscribeQtyChange = subscribe(
        EVENTS.qtyChange,
        this.handleQtyChange.bind(this)
      );
    }

    this.removeButton?.addEventListener("click", this.handleRemove.bind(this));
  }

  disconnectedCallback() {
    this.unsubscribeQtyChange?.();
  }

  handleQtyChange(event) {
    const { value, id } = event.detail;
    if (id === this.key) {
      window.iaCartAPI?.updateQty({ key: this.key, qty: value, _ts: this.ts });
    }
  }

  handleRemove() {
    window.iaCartAPI?.updateQty({ key: this.key, qty: 0, _ts: this.ts });
  }
}

class IaCartBubble extends HTMLElement {
  connectedCallback() {
    this.unsubscribeCartChange = subscribe(
      EVENTS.cartChanged,
      this.handleCartChange.bind(this)
    );
  }

  disconnectedCallback() {
    this.unsubscribeCartChange?.();
  }

  handleCartChange(event) {
    // console.log("handleCartChange", event);
    const cartCount = event.detail?.cartObj?.item_count || 0;
    if (cartCount !== undefined) {
      this.textContent = cartCount;
      if (cartCount === 0) {
        this.classList.add("ia-hidden");
      } else {
        this.classList.remove("ia-hidden");
      }
    }
  }
}

class IaCartNote extends HTMLElement {
  connectedCallback() {
    this.noteField = this.querySelector('[data-note-field="note"]');
    
    // Handle save button
    this.querySelector(".js-note-update")?.addEventListener(
      "click",
      this.handleNoteUpdate.bind(this)
    );
  }
  
  async handleNoteUpdate(event) {
    event.preventDefault();

    const note = this.noteField?.value || "";
    
    try {
      await window.iaCartAPI?.updateCart({
        data: { note }
      });
    } catch (error) {
      // console.error("Error updating cart note:", error);
    }
  }
}


class IaCartPlanPicker extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    
    if (this.hasAttribute("data-add-plan")) {
      this.addEventListener("click", this.handleAddPlan.bind(this));
    }
    
    const planSelect = this.querySelector("[data-plan-select]");
    if (planSelect) {
      planSelect.addEventListener("change", this.handlePlanChange.bind(this));
    }
  }

  getQuantity() {
    const cartItem = this.closest("ia-cart-item");
    return parseInt(cartItem?.dataset.qty || 1, 10);
  }

  async handleAddPlan(event) {
    event.preventDefault();
    const planId = this.dataset.addPlan;
    if (planId && this.key) {
      await window.iaCartAPI?.changePlan({ 
        id: this.key, 
        quantity: this.getQuantity(), 
        planId 
      });
    }
  }

  async handlePlanChange(event) {
    const planId = event.target.value === "one-time" ? null : event.target.value;
    if (this.key) {
      await window.iaCartAPI?.changePlan({ 
        id: this.key, 
        quantity: this.getQuantity(), 
        planId 
      });
    }
  }
}

class IaCartProgressBar extends HTMLElement {
  connectedCallback() {
    this.targetPrice = this.dataset.targetPrice;
    this.unsubscribeCartChange = subscribe(
      EVENTS.cartChanged,
      this.render.bind(this)
    );
  }
  render(event) {
    const { cartObj, cartDom } = event.detail;

    cartObj.item_count === 0 ? this.style.display = "none" : this.style.display = "block";

    const progress = cartDom.querySelector("[data-progress-bar-current]").style.width;
    
    this.querySelector("[data-progress-bar-current]").style.width = progress;
    this.querySelector("[data-progress-heading]").innerHTML = cartDom.querySelector("[data-progress-heading]").innerHTML;
  }
}

customElements.define("ia-cart", IaCart);
customElements.define("ia-cart-item", IaCartItem);
customElements.define("ia-cart-bubble", IaCartBubble);
customElements.define("ia-cart-note", IaCartNote);
customElements.define("ia-cart-plan-picker", IaCartPlanPicker);
customElements.define("ia-cart-progress-bar", IaCartProgressBar);

